<div class="sm-page">
  <header class="sm-brand">
    <div class="sm-brand__mark">A</div>
    <span class="sm-brand__name">Patient Support</span>
    <span class="sm-brand__tag">
      Secure messaging
    </span>
  </header>

  <main class="sm-main">
    <section *ngIf="stage === 'loading'" class="sm-card" aria-busy="true">
      <p class="sm-muted">Opening your secure message…</p>
    </section>

    <section *ngIf="stage === 'expired'" class="sm-card">
      <h1>This link has expired</h1>
      <p class="sm-lead">Ask for a new link to continue. The message body is never sent by email.</p>
      <button type="button" class="sm-btn sm-btn--primary" [disabled]="busy" (click)="requestNewLink()">Ask for a new link</button>
      <p *ngIf="info" class="sm-info">{{ info }}</p>
      <p *ngIf="error" class="sm-error">{{ error }}</p>
    </section>

    <section *ngIf="stage === 'session-expired'" class="sm-card">
      <h1>Session ended</h1>
      <p class="sm-lead">{{ info || "Verify again to open the message room." }}</p>
      <button type="button" class="sm-btn sm-btn--primary" (click)="backToVerify()">Verify again</button>
    </section>

    <section *ngIf="stage === 'verify'" class="sm-card sm-card--narrow">
      <p class="sm-ref" *ngIf="resolve?.public_code">Reference {{ resolve?.public_code }}</p>
      <h1>Verify it is you</h1>
      <p class="sm-lead">This message contains patient information. We confirm who you are before showing it.</p>
      <p class="sm-muted" *ngIf="resolve?.recipient_email_mask">Named recipient · {{ resolve?.recipient_email_mask }}</p>

      <label class="sm-field">
        <span>Work email address</span>
        <input type="email" class="sm-input" autocomplete="email" [(ngModel)]="workEmail" [disabled]="busy" />
      </label>

      <button type="button" class="sm-btn sm-btn--primary" [disabled]="!canSendOtp" (click)="sendOtp()">
        {{ otpSent ? "Resend passcode" : "Send one-time passcode" }}
      </button>
      <p *ngIf="otpSent" class="sm-info">{{ otpHint }}</p>

      <label class="sm-field" *ngIf="otpSent">
        <span>One-time passcode</span>
        <input
          type="text"
          class="sm-input"
          inputmode="numeric"
          maxlength="6"
          autocomplete="one-time-code"
          [(ngModel)]="otpCode"
          [disabled]="busy"
        />
      </label>

      <button *ngIf="otpSent" type="button" class="sm-btn sm-btn--primary" [disabled]="!canVerify" (click)="verifyOtp()">
        Verify and open
      </button>

      <aside class="sm-why">
        <div>
          <strong>Why you are being asked</strong>
          <p>The message contains patient information. We verify who you are before showing it.</p>
        </div>
        <div>
          <strong>Access is recorded</strong>
          <p>Your organisation, name and the time are logged as a disclosure.</p>
        </div>
      </aside>

      <p *ngIf="error" class="sm-error">{{ error }}</p>
      <p class="sm-footer-note">This message is held securely and is not sent by email. Access is recorded.</p>
    </section>

    <section *ngIf="stage === 'room' && room" class="sm-room">
      <header class="sm-room__head">
        <div>
          <h1>Secure message</h1>
          <p class="sm-muted">Reference {{ room.public_code }} · {{ room.party_nm }}</p>
        </div>
        <span class="sm-idle-note">Expires after 20 minutes of inactivity</span>
      </header>

      <div class="sm-room__grid">
        <div class="sm-thread" #threadScroll>
          <article
            *ngFor="let m of room.messages"
            class="sm-msg"
            [class.is-mine]="m.is_mine"
          >
            <header class="sm-msg__head">
              <strong>{{ m.is_mine ? ("You · " + (room.party_nm || "practice")) : (m.author_nm || "Patient support") }}</strong>
              <time *ngIf="m.ts">{{ m.ts | date: "medium" }}</time>
            </header>
            <p class="sm-msg__body">{{ m.body_txt }}</p>
            <ul *ngIf="m.attachment_nms?.length" class="sm-msg__files">
              <li *ngFor="let f of m.attachment_nms">{{ f }}</li>
            </ul>
          </article>

          <p *ngIf="!room.messages?.length" class="sm-muted">No messages in this thread yet.</p>
          <p *ngIf="replySent" class="sm-info">Reply sent. Your reply stays in this secure area.</p>

          <div class="sm-composer">
            <div class="sm-quick" *ngIf="room.quick_answers?.length">
              <span class="sm-quick__label">Quick answers</span>
              <div class="sm-quick__chips">
                <button
                  type="button"
                  *ngFor="let q of room.quick_answers"
                  class="sm-chip"
                  [class.is-on]="quickAnswerCd === q.answer_cd"
                  (click)="pickQuick(q.answer_cd)"
                >
                  {{ q.answer_nm }}
                </button>
              </div>
            </div>

            <textarea
              class="sm-textarea"
              rows="3"
              placeholder="Write a reply…"
              [(ngModel)]="replyBody"
              [disabled]="busy"
            ></textarea>

            <div class="sm-composer__bar">
              <label class="sm-attach">
                Attach a document
                <input type="file" accept=".pdf,.jpg,.jpeg,.png" multiple (change)="onFilesSelected($event)" />
              </label>
              <button type="button" class="sm-btn sm-btn--primary" [disabled]="!canReply" (click)="sendReply()">Send reply</button>
            </div>
            <ul *ngIf="files.length" class="sm-msg__files">
              <li *ngFor="let f of files; let i = index">
                {{ f.name }}
                <button type="button" class="sm-link" (click)="removeFile(i)">Remove</button>
              </li>
            </ul>
            <p *ngIf="attachmentError" class="sm-error">{{ attachmentError }}</p>
            <p class="sm-muted">Your reply stays in this secure area. It is not sent by email.</p>
            <p *ngIf="error" class="sm-error">{{ error }}</p>
            <p *ngIf="info" class="sm-info">{{ info }}</p>
          </div>
        </div>

        <aside class="sm-side">
          <div>
            <strong>About this message</strong>
            <p>Reference {{ room.public_code }} · programme support</p>
          </div>
          <div>
            <strong>Session</strong>
            <p>Expires after 20 minutes of inactivity</p>
          </div>
          <div class="sm-side__warn" *ngIf="room.patient_ref">
            <strong>Patient reference</strong>
            <p>{{ room.patient_ref }}</p>
            <p>Shown only after verification, and only to named recipients at this practice.</p>
          </div>
          <div>
            <strong>Back in SCRAII</strong>
            <p>Your reply is captured as an inbound interaction on the case.</p>
          </div>
        </aside>
      </div>

      <p class="sm-footer-note">This message is held securely and is not sent by email. Access is recorded.</p>
    </section>
  </main>
</div>
